import { callLogEndpoint } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { queryClient } from "@/lib/queryClient";
import { useInfiniteQuery } from "@tanstack/react-query";

interface UseCallLogsParams {
  leadId?: string | number | null;
  status?: string;
}

export function useCallLogs({ leadId, status }: UseCallLogsParams) {
  const endpoint = `${callLogEndpoint}/lead/${leadId}`;
  const enabled = Boolean(leadId);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["call-logs", leadId, status],
    enabled,
    queryFn: async ({ pageParam = 1 }) => {
      const params: Record<string, any> = {
        page: pageParam,
        limit: 15,
      };
      if (status && status !== "All") {
        params.status = status;
      }
      const response = await axiosInstance.get(endpoint, { params });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) => {
      const logs = lastPage?.logs ?? [];
      if (logs.length < 15) return undefined;
      return allPages.length + 1;
    },
    select: (data) => ({
      logs: data.pages.flatMap((page: any) => page.logs || []),
    }),
  });

  return {
    logs: data?.logs ?? [],
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}

export const refetchCallLogs = () => {
  queryClient.invalidateQueries({ queryKey: ["call-logs"] });
};
